import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { IUser } from '../../types/damTypes';
import { apiErrorMessage, launchApi } from './api';
import { makeId, today } from './helpers';
import { fieldHelp } from './helpTexts';
import { Assignee, CalendarWindow, YearbookSuggestion } from './types';
import { windowLabel } from './yearbook/labels';

interface Props {
  open: boolean;
  currentUser: IUser | null;
  assignees: Assignee[];
  onClose: () => void;
  onCreated: (launchId: string, activationSkipped: boolean, activationError?: string) => void;
}

interface ProductRow { id: string; name: string; articleNumber: string }
interface ChainRow { id: string; chain: string; announcement: string; calendarWindowId: string | null; calendarWindowVersion: number | null; calendarCategories: string[] }
interface RoleRow { id: string; role: string; userId: string }

const steps = ['Grunduppgifter', 'Produkter', 'Kedjor', 'Roller', 'Granska'];

const LaunchWizard: React.FC<Props> = ({ open, currentUser, assignees, onClose, onCreated }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [ownerUserId, setOwnerUserId] = useState(currentUser?._id || '');
  const [products, setProducts] = useState<ProductRow[]>([{ id: makeId('product'), name: '', articleNumber: '' }]);
  const [chains, setChains] = useState<ChainRow[]>([{ id: makeId('chain'), chain: '', announcement: today(), calendarWindowId: null, calendarWindowVersion: null, calendarCategories: [] }]);
  const [suggestions, setSuggestions] = useState<Record<string, YearbookSuggestion[]>>({});
  const [pickedProducts, setPickedProducts] = useState<Record<string, string[]>>({});
  const [calendarWindows, setCalendarWindows] = useState<CalendarWindow[]>([]);
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [templateRoles, setTemplateRoles] = useState<string[]>([]);
  const [chainOptions, setChainOptions] = useState<string[]>([]);
  const [activate, setActivate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && !ownerUserId && currentUser?._id) setOwnerUserId(currentUser._id);
  }, [currentUser, open, ownerUserId]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    launchApi.templateRoles()
      .then((next) => { if (!cancelled) setTemplateRoles(next); })
      .catch(() => { if (!cancelled) setTemplateRoles([]); });
    launchApi.settings()
      .then((settings) => {
        if (cancelled) return;
        setChainOptions(settings.chains.filter((chain) => chain.active).map((chain) => chain.name));
        const defaults = Object.entries(settings.roleDefaults).filter(([, userId]) => userId);
        if (defaults.length) {
          setRoles((current) => (current.length ? current : defaults.map(([role, userId]) => ({ id: makeId('role'), role, userId }))));
        }
      })
      .catch(() => { if (!cancelled) setChainOptions([]); });
    launchApi.calendarWindows()
      .then((next) => { if (!cancelled) setCalendarWindows(next); })
      .catch(() => { if (!cancelled) setCalendarWindows([]); });
    return () => { cancelled = true; };
  }, [open]);

  const valid = useMemo(() => [
    Boolean(name.trim() && ownerUserId),
    products.length > 0 && products.every((product) => product.name.trim()),
    chains.length > 0 && chains.every((chain) => chain.chain.trim() && chain.announcement),
    roles.every((role) => role.role.trim() && role.userId),
    true,
  ], [chains, name, ownerUserId, products, roles]);

  const resetAndClose = () => {
    if (busy) return;
    setStep(0);
    setError(null);
    onClose();
  };

  const create = async () => {
    setBusy(true);
    setError(null);
    const launchId = makeId('launch');
    const idempotencyKey = makeId('create');
    try {
      const created = await launchApi.create({
        id: launchId,
        name: name.trim(),
        description: description.trim(),
        ownerUserId,
        products: products.map((product) => ({
          id: product.id,
          name: product.name.trim(),
          ...(product.articleNumber.trim() ? { articleNumber: product.articleNumber.trim() } : {}),
        })),
        chains: chains.map((chain) => ({
          id: chain.id,
          chain: chain.chain.trim(),
          announcement: chain.announcement,
          ...(chain.calendarWindowId ? { calendarWindowId: chain.calendarWindowId, calendarWindowVersion: chain.calendarWindowVersion } : {}),
          ...(chain.calendarCategories.length ? { calendarCategories: chain.calendarCategories } : {}),
        })),
        roleUsers: Object.fromEntries(roles.map((role) => [role.role.trim(), role.userId])),
        idempotencyKey,
      });
      let activationSkipped = false;
      let activationError: string | undefined;
      if (activate) {
        try {
          const workspace = await launchApi.workspace(created.id);
          if (workspace.overview.scheduleCounts.overdue > 0) {
            activationSkipped = true;
          } else {
            await launchApi.launchCommand(created.id, {
              type: 'activate',
              commandId: makeId('activate'),
              expectedVersion: workspace.expectedVersion,
              actuallyOccurredAt: today(),
            });
          }
        } catch (activationRequestError) {
          activationSkipped = true;
          activationError = apiErrorMessage(activationRequestError, 'Lanseringen skapades men kunde inte aktiveras.');
        }
      }
      setStep(0);
      setName('');
      setDescription('');
      setProducts([{ id: makeId('product'), name: '', articleNumber: '' }]);
      setChains([{ id: makeId('chain'), chain: '', announcement: today(), calendarWindowId: null, calendarWindowVersion: null, calendarCategories: [] }]);
      setRoles([]);
      setActivate(false);
      onCreated(created.id, activationSkipped, activationError);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Lanseringen kunde inte skapas.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={resetAndClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>Skapa ny lansering</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} alternativeLabel={!fullScreen} orientation={fullScreen ? 'vertical' : 'horizontal'} sx={{ my: 2 }}>
          {steps.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === 0 && (
          <Stack spacing={2}>
            <TextField required autoFocus label="Lanseringens namn" value={name} onChange={(event) => setName(event.target.value)} />
            <TextField label="Beskrivning" value={description} multiline minRows={3} onChange={(event) => setDescription(event.target.value)} />
            <TextField required select label="Lanseringsägare" helperText={fieldHelp.owner} value={ownerUserId} onChange={(event) => setOwnerUserId(event.target.value)}>
              {currentUser && !assignees.some((person) => person.id === currentUser._id) && (
                <MenuItem value={currentUser._id}>{[currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ') || currentUser.email}</MenuItem>
              )}
              {assignees.map((person) => <MenuItem key={person.id} value={person.id}>{person.name} · {person.email}</MenuItem>)}
            </TextField>
          </Stack>
        )}

        {step === 1 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">Produkterna är lokala för lanseringen. Artikelnummer är valfritt.</Typography>
            {products.map((product, index) => (
              <Stack key={product.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <TextField required fullWidth label={`Produkt ${index + 1}`} value={product.name} onChange={(event) => setProducts((rows) => rows.map((row) => row.id === product.id ? { ...row, name: event.target.value } : row))} />
                <TextField fullWidth label="Artikelnummer" value={product.articleNumber} onChange={(event) => setProducts((rows) => rows.map((row) => row.id === product.id ? { ...row, articleNumber: event.target.value } : row))} />
                <IconButton aria-label={`Ta bort produkt ${index + 1}`} disabled={products.length === 1} onClick={() => setProducts((rows) => rows.filter((row) => row.id !== product.id))}><DeleteOutlineIcon /></IconButton>
              </Stack>
            ))}
            <Button startIcon={<AddIcon />} onClick={() => setProducts((rows) => [...rows, { id: makeId('product'), name: '', articleNumber: '' }])}>Lägg till produkt</Button>
          </Stack>
        )}

        {step === 2 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">Lägg till de kedjor som gäller. Aviseringsdatum är vecka 0 för respektive kedja.</Typography>
            {chains.map((chain, index) => (
              <Stack key={chain.id} spacing={1}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                {chainOptions.length > 0 ? (
                  <TextField required select fullWidth label={`Kedja ${index + 1}`} value={chain.chain} onChange={(event) => {
                    setChains((rows) => rows.map((row) => row.id === chain.id ? { ...row, chain: event.target.value, calendarWindowId: null, calendarWindowVersion: null, calendarCategories: [] } : row));
                    setSuggestions((current) => ({ ...current, [chain.id]: [] }));
                    setPickedProducts((current) => ({ ...current, [chain.id]: [] }));
                  }}>
                    <MenuItem value="">Välj kedja</MenuItem>
                    {chainOptions.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
                  </TextField>
                ) : (
                  <TextField required fullWidth label={`Kedja ${index + 1}`} value={chain.chain} onChange={(event) => {
                    setChains((rows) => rows.map((row) => row.id === chain.id ? { ...row, chain: event.target.value, calendarWindowId: null, calendarWindowVersion: null, calendarCategories: [] } : row));
                    setSuggestions((current) => ({ ...current, [chain.id]: [] }));
                    setPickedProducts((current) => ({ ...current, [chain.id]: [] }));
                  }} />
                )}
                {calendarWindows.some((window) => window.chain.toLocaleLowerCase('sv') === chain.chain.toLocaleLowerCase('sv')) && (
                  <TextField select fullWidth label="Kalenderfönster" value={chain.calendarWindowId || ''} onChange={(event) => {
                    const selected = calendarWindows.find((window) => window.familyId === event.target.value);
                    setChains((rows) => rows.map((row) => row.id === chain.id ? {
                      ...row,
                      calendarWindowId: selected?.familyId || null,
                      calendarWindowVersion: selected?.version || null,
                      announcement: selected?.announcement || row.announcement,
                      calendarCategories: [],
                    } : row));
                    setSuggestions((current) => ({ ...current, [chain.id]: [] }));
                    setPickedProducts((current) => ({ ...current, [chain.id]: [] }));
                  }}>
                    <MenuItem value="">Eget datum</MenuItem>
                    {calendarWindows.filter((window) => window.chain.toLocaleLowerCase('sv') === chain.chain.toLocaleLowerCase('sv')).map((window) => (
                      <MenuItem key={window.familyId} value={window.familyId}>{windowLabel(window)}</MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField required fullWidth type="date" label="Aviseringsdatum" helperText={fieldHelp.announcement} value={chain.announcement} InputLabelProps={{ shrink: true }} onChange={(event) => setChains((rows) => rows.map((row) => row.id === chain.id ? { ...row, announcement: event.target.value, calendarWindowId: null, calendarWindowVersion: null, calendarCategories: [] } : row))} />
                <IconButton aria-label={`Ta bort kedja ${index + 1}`} disabled={chains.length === 1} onClick={() => setChains((rows) => rows.filter((row) => row.id !== chain.id))}><DeleteOutlineIcon /></IconButton>
              </Stack>
                {(calendarWindows.find((window) => window.familyId === chain.calendarWindowId)?.categories || []).length > 0 && (
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ width: '100%' }}>
                    {(calendarWindows.find((window) => window.familyId === chain.calendarWindowId)?.categories || []).map((category) => {
                      const active = chain.calendarCategories.includes(category);
                      return (
                        <Button key={category} sx={{ borderRadius: 16, textTransform: 'none' }} variant={active ? 'contained' : 'outlined'} onClick={() => {
                          const next = active ? chain.calendarCategories.filter((item) => item !== category) : [...chain.calendarCategories, category];
                          setChains((rows) => rows.map((row) => row.id === chain.id ? { ...row, calendarCategories: next } : row));
                          if (!chain.calendarWindowId || next.length === 0) {
                            setSuggestions((current) => ({ ...current, [chain.id]: [] }));
                            return;
                          }
                          launchApi.yearbookSuggestions(chain.calendarWindowId, next)
                            .then((products) => setSuggestions((current) => ({ ...current, [chain.id]: products })))
                            .catch(() => setSuggestions((current) => ({ ...current, [chain.id]: [] })));
                        }}>{category}</Button>
                      );
                    })}
                  </Stack>
                )}
                {(suggestions[chain.id] || []).length > 0 && (
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="body2" color="text.secondary">{suggestions[chain.id].length === 1 ? '1 produkt i årskalendern' : `${suggestions[chain.id].length} produkter i årskalendern`}</Typography>
                    {suggestions[chain.id].map((product) => (
                      <FormControlLabel
                        key={product.id}
                        control={<Checkbox checked={(pickedProducts[chain.id] || []).includes(product.id)} onChange={(event) => {
                          setPickedProducts((current) => {
                            const selected = current[chain.id] || [];
                            return { ...current, [chain.id]: event.target.checked ? [...selected, product.id] : selected.filter((id) => id !== product.id) };
                          });
                        }} />}
                        label={`${product.articleNumber ? `${product.articleNumber} ` : ''}${product.articleName}`}
                      />
                    ))}
                    <Typography variant="caption" color="text.secondary">Lägg till i lanseringen</Typography>
                  </Box>
                )}
              </Stack>
            ))}
            <Button startIcon={<AddIcon />} onClick={() => setChains((rows) => [...rows, { id: makeId('chain'), chain: '', announcement: today(), calendarWindowId: null, calendarWindowVersion: null, calendarCategories: [] }])}>Lägg till kedja</Button>
          </Stack>
        )}

        {step === 3 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">Tilldela kända beslutsroller. Roller som inte är kända ännu kan lämnas till senare. {fieldHelp.roles}</Typography>
            {roles.map((role, index) => (
              <Stack key={role.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <TextField required select fullWidth label={`Roll ${index + 1}`} value={role.role} onChange={(event) => setRoles((rows) => rows.map((row) => row.id === role.id ? { ...row, role: event.target.value } : row))}>
                  <MenuItem value="">Välj roll</MenuItem>
                  {templateRoles.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
                </TextField>
                <TextField required fullWidth select label="Person" value={role.userId} onChange={(event) => setRoles((rows) => rows.map((row) => row.id === role.id ? { ...row, userId: event.target.value } : row))}>
                  <MenuItem value="">Välj person</MenuItem>
                  {assignees.map((person) => <MenuItem key={person.id} value={person.id}>{person.name}</MenuItem>)}
                </TextField>
                <IconButton aria-label={`Ta bort roll ${index + 1}`} onClick={() => setRoles((rows) => rows.filter((row) => row.id !== role.id))}><DeleteOutlineIcon /></IconButton>
              </Stack>
            ))}
            <Button startIcon={<AddIcon />} onClick={() => setRoles((rows) => [...rows, { id: makeId('role'), role: '', userId: '' }])}>Lägg till roll</Button>
          </Stack>
        )}

        {step === 4 && (
          <Stack spacing={1.5}>
            <Typography variant="h6">{name}</Typography>
            {description && <Typography>{description}</Typography>}
            <Typography>Ägare: {assignees.find((person) => person.id === ownerUserId)?.name || currentUser?.email || ownerUserId}</Typography>
            <Typography>Produkter: {products.map((product) => product.name).join(', ')}</Typography>
            <Typography>Kedjor: {chains.map((chain) => `${chain.chain} (${chain.announcement})`).join(', ')}</Typography>
            <Typography>Roller: {roles.length ? roles.map((role) => role.role).join(', ') : 'Tilldelas senare'}</Typography>
            <Box>
              <FormControlLabel
                control={<Checkbox checked={activate} onChange={(event) => setActivate(event.target.checked)} />}
                label="Aktivera direkt efter att utkastet skapats"
              />
              <Typography variant="caption" display="block" color="text.secondary">Ägare, minst en produkt och minst en kedja är angivna, så utkastet kan aktiveras.</Typography>
              <Typography variant="caption" display="block" color="text.secondary">Om någon uppgift redan har passerat sin deadline aktiveras lanseringen inte direkt. Den skapas som utkast så att du kan kontrollera aviseringsdatum först.</Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose}>Avbryt</Button>
        {step > 0 && <Button disabled={busy} onClick={() => setStep((value) => value - 1)}>Tillbaka</Button>}
        {step < steps.length - 1
          ? <Button variant="contained" disabled={!valid[step]} onClick={() => {
            if (step === 2) {
              setProducts((current) => {
                const extras: ProductRow[] = [];
                chains.forEach((chain) => {
                  const chosen = new Set(pickedProducts[chain.id] || []);
                  (suggestions[chain.id] || []).forEach((product) => {
                    if (!chosen.has(product.id)) return;
                    const number = product.articleNumber.trim();
                    const exists = [...current, ...extras].some((item) => number && item.articleNumber.trim() === number);
                    if (!exists) extras.push({ id: makeId('product'), name: product.articleName, articleNumber: number });
                  });
                });
                const meaningful = current.filter((item) => item.name.trim());
                return extras.length ? [...meaningful, ...extras] : current;
              });
            }
            setStep((value) => value + 1);
          }}>Nästa</Button>
          : <Button variant="contained" disabled={busy || !valid.every(Boolean)} onClick={create}>{busy ? 'Skapar…' : activate ? 'Skapa och aktivera' : 'Skapa utkast'}</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default LaunchWizard;
